import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { getSearchParam } from "Utils/url";
import "Assets/css/Connect.css"
import refererApi from "Apis/refererApi";
import TcktLstApi from "Apis/TcktLstApi";
import TcktUseApi from "Apis/TcktUseApi";
import TcktApi from "Apis/TcktApi";

interface TcktItem {
    prdtNo: number;
    prdtImg: string;
    evntCd: string;
    prdtNm: string;
    winnerCnt: number;
    needTcktCnt: number;
    youCnt: number;
}

interface EventModalContent {
    h1: string;
    p: string;
    btnLabel: string;
}

interface TcktUseHist {
    useDtime: string;
    prdtNm: string;
    tcktCnt: number;
}

interface TcktSummary {
    tcktCnt: number;
    tcktUseLst: TcktUseHist[];
}

const MOBILE_LOGIN_HDR_PADDING_RATIO = 50 / 213;
const EVENT_END_AT_UTC_MS = Date.UTC(2026, 4, 13, 15, 0, 0);



const CafeEvntUi =() => {
   const location = useLocation();
    const isEventEnded = Date.now() >= EVENT_END_AT_UTC_MS;
    const loginInfoState = useSelector((state: any) => state.LoginInfoState?.LoginInfo?.apiBody);
    const isLogin = !!loginInfoState?.login;
    const idntMthdCd = loginInfoState?.idntMthdCd;
    const loginReferrer = encodeURIComponent(`${location.pathname}${location.search}`);
   const [tcktLstState, setTcktLstState] = useState<TcktItem[]>([]);
   const [resultModalOpen, setResultModalOpen] = useState(false);
   const [resultModalContent, setResultModalContent] = useState<EventModalContent>({
        h1: "응모 안내",
        p: "",
        btnLabel: "확인",
   });
   const [tcktHistModalOpen, setTcktHistModalOpen] = useState(false);
   const [tcktSummaryState, setTcktSummaryState] = useState<TcktSummary>({
       tcktCnt: 0,
       tcktUseLst: [],
   });
   const [mobileLoginHdrHeight, setMobileLoginHdrHeight] = useState<number | null>(null);
   const tcktIsLoginMoBgRef = useRef<HTMLImageElement | null>(null);
   const [modalAction, setModalAction] = useState<(() => void) | null>(null);
   const getCurrentParams = () => {
        return {
            currentGameSvcCd: getSearchParam(location.search, "gameSvcCd"),
            currentCtntCtgrCd: getSearchParam(location.search, "ctntKindCd"),
        };
    };
    const currentParams = useMemo(() => getCurrentParams(), [location.search]);
    const mobileLoginHdrStyle = useMemo<React.CSSProperties | undefined>(() => {
        if (!mobileLoginHdrHeight) {
            return undefined;
        }

        const computedPaddingTop = Math.round(mobileLoginHdrHeight * MOBILE_LOGIN_HDR_PADDING_RATIO);
        return {
            height: `${mobileLoginHdrHeight}px`,
            paddingTop: `${computedPaddingTop}px`,
        };
    }, [mobileLoginHdrHeight]);

    const openResultModal = (content: Partial<EventModalContent>, action?: () => void) => {
        setResultModalContent({
            h1: content.h1 || "응모 안내",
            p: content.p || "",
            btnLabel: content.btnLabel || "확인",
        });
        setModalAction(() => action || null);
        setResultModalOpen(true);
    };

    const closeResultModal = () => {
        setResultModalOpen(false);
        const action = modalAction;
        setModalAction(null);
        if (action) action();
    };

    const dismissResultModal = () => {
        setResultModalOpen(false);
        setModalAction(null);
    };

    const openTcktHistModal = () => {
        setTcktHistModalOpen(true);
    };

    const closeTcktHistModal = () => {
        setTcktHistModalOpen(false);
    };

    const fetchTcktList = () => {
        TcktLstApi().then((resp) => {
            const list = Array.isArray(resp?.apiBody)
                ? resp.apiBody
                : Array.isArray(resp?.data)
                    ? resp.data
                    : (Array.isArray(resp) ? resp : []);
            setTcktLstState(list);
        });
    };

    const refreshTcktSummary = () => {
        TcktApi().then((resp) => {
            const apiBody = resp?.apiBody;
            setTcktSummaryState({
                tcktCnt: typeof apiBody?.tcktCnt === "number" ? apiBody.tcktCnt : 0,
                tcktUseLst: Array.isArray(apiBody?.tcktUseLst) ? apiBody.tcktUseLst : [],
            });
        });
    };

    const refreshTcktData = () => {
        refreshTcktSummary();
        refererApi();
        fetchTcktList();
    };

    const TcktUseFunc = (evntCd:string) => {
        TcktUseApi(evntCd)
        .then((resp)=>{
            switch(resp.apiHdr.code) {
                case "MEMB0000": {
                    openResultModal(
                        {
                            h1: "응모 완료",
                            p: "응모가 정상적으로 완료되었습니다.",      
                            btnLabel: "확인",
                        }
                    );
                    return;
                }
                case "MEMB3211" : {
                    openResultModal({
                        h1: "응모 실패",
                        p: "사용 가능한 응모권의 수가 부족합니다.",
                        btnLabel: "확인",
                        }
                    );
                    return;
                }
                default:
                    openResultModal({
                        h1: "",
                        p: resp?.apiHdr?.msg || "응모 기간이 종료되었습니다.",
                        btnLabel: "확인",
                        }
                    );
                    return;
            }
        })
        .catch((err)=>{
            openResultModal({
                h1: "응모 실패",
                p: err?.response?.data?.message || "응모 신청 중 오류가 발생했습니다.",
                btnLabel: "확인",
            });
        })
        .finally(() => {
            refreshTcktData();
        });
    };

    useEffect(() => {
        if (!resultModalOpen && !tcktHistModalOpen) {
            return;
        }

        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = originalOverflow;
        };
    }, [resultModalOpen, tcktHistModalOpen]);

    useEffect(() => {
        const updateMobileLoginHdrHeight = () => {
            if (!isLogin || typeof window === "undefined") {
                setMobileLoginHdrHeight(null);
                return;
            }

            const isMobileViewport = window.matchMedia("(max-width: 700px)").matches;
            if (!isMobileViewport) {
                setMobileLoginHdrHeight(null);
                return;
            }

            const imgHeight = tcktIsLoginMoBgRef.current?.getBoundingClientRect().height || 0;
            setMobileLoginHdrHeight(imgHeight > 0 ? imgHeight : null);
        };

        updateMobileLoginHdrHeight();
        window.addEventListener("resize", updateMobileLoginHdrHeight);
        return () => {
            window.removeEventListener("resize", updateMobileLoginHdrHeight);
        };
    }, [isLogin]);

    useEffect(() => {
        refreshTcktData();
    }, [currentParams]);
    return(
        <div className="ConnectEventBg">
            <div className="ConnectEventTopBanner">
                <img src="https://bb-cdn.onuniverse.io/web/event/Connect/MissionEventTopBannerMo.png" alt="" className="Block700"/> 
            </div>
            <div className="Container940">
                <div className="connectEventListWrap">
                    <div className="connectEventListHeader">
                        {isLogin ? (
                            <div
                                className="tcktHdrWrap isLogin"
                                style={mobileLoginHdrStyle}
                            >
                                <img
                                    src={process.env.PUBLIC_URL + "/images/Connect/tcktIsLoginMo.png"}
                                    alt=""
                                    className="tcktIsLoginMoBg"
                                    ref={tcktIsLoginMoBgRef}
                                    onLoad={() => {
                                        const imgHeight = tcktIsLoginMoBgRef.current?.getBoundingClientRect().height || 0;
                                        setMobileLoginHdrHeight(imgHeight > 0 ? imgHeight : null);
                                    }}
                                />
                                <div className={`myTckt${isEventEnded ? " disabled" : ""}`}>
                                    <p>{isEventEnded ? "종료된 이벤트입니다" : "사용 가능한 응모권 수"}</p>
                                    <h2>
                                        {isEventEnded
                                            ? "* 당첨자 안내 홈페이지 별도 공지 예정입니다 *"
                                            : `${tcktSummaryState.tcktCnt}개`}
                                    </h2>
                                </div>
                                <button type="button" className="tcktLstBtn" onClick={openTcktHistModal}></button>
                            </div>
                        ) : (
                            <div className="tcktHdrWrap noLogin">
                                <Link to={`/Login?referrer=${loginReferrer}`} className="tcktUseNoLogin">
                                </Link>
                            </div>
                        )}
                    </div>
                    <ul className="connectEventCardList">
                        {tcktLstState.map((item) => (
                            <li key={item.prdtNo} className="connectEventCardItem">
                                <div className="connectEventCardThumb">
                                    {item.prdtImg ? (
                                        <img src={item.prdtImg} alt={item.prdtNm} />
                                    ) : null}
                                    <h3 className="connectEventCardTitle connectEventCardTitleMo">{item.prdtNm}</h3>
                                </div>

                                <div className="connectEventCardBody">
                                    <h3 className="connectEventCardTitle connectEventCardTitlePc">{item.prdtNm}</h3>
                                    <dl className="connectEventCardInfo">
                                        <div className="connectEventCardInfoRow">
                                            <dt>추첨 인원</dt>
                                            <dd>{item.winnerCnt}명</dd>
                                        </div>
                                        <div className="connectEventCardInfoRow">
                                            <dt>필요 응모권 수</dt>
                                            <dd>{item.needTcktCnt}개</dd>
                                        </div>
                                        <div className="connectEventCardInfoRow">
                                            <dt>나의 응모 횟수</dt>
                                            <dd>{item.youCnt}번</dd>
                                        </div>
                                    </dl>
                                    
                                    <button
                                        type="button"
                                        className="connectEventApplyBtn"
                                        onClick={() => TcktUseFunc(item.evntCd)}
                                        disabled={isEventEnded}
                                    >
                                        {isEventEnded ? "응모 기간이 종료되었습니다" : "응모하기"}
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                    <div className="eventNotiWrap">
                        <div className="eventNotiUl">
                            <ul>
                                <li>동일 상품에 대한 중복 응모가 가능하며, 응모 횟수가 많을수록 해당 상품의 당첨 확률이 높아집니다. </li>
                                <li>획득한 응모권은 원하는 상품에 자유롭게 사용할 수 있으며, 동일 상품에 여러 번 응모하거나 복수의 상품에 나누어 응모할 수 있습니다.</li>
                                <li>본 이벤트의 당첨자는 이벤트 종료 후 일괄 추첨 및 발표됩니다. </li>
                                <li>게임 아이템 외 실물 경품 및 쿠폰 상품은 중복 당첨이 제한될 수 있으며, 중복 당첨 확인 시 일부 당첨이 취소 또는 조정될 수 있습니다. </li>
                                <li>[중복 당첨 예시]</li>
                                <li>커넥트 커피 세트 + BB 로스트 커피 10개 : 중복 당첨 가능 </li>
                                <li>BB 로스트 커피 10개 + BB 버터 샌드 10개 : 중복 당첨 가능</li>
                                <li> 커넥트 커피 세트 + 네이버페이 1만원 : 중복 당첨 불가 (‘필요 응모권 수’가 더 많이 필요한 선물이 선정)</li>
                            </ul>
                        </div>
                    </div>
                    <div className="eventNotiWrap">
                        <p>유의사항</p>
                        {isLogin && (
                            <div className="idntBtn">
                                {idntMthdCd === "NA" ? (
                                    <Link to={"/Mypage"}>
                                        <p>인증 바로가기</p>
                                    </Link>
                                ) : (
                                    <p>인증 완료</p>
                                )}
                            </div>
                        )}
                        <div className="eventNotiUl bgNone">
                            <ul>
                                <li>본 이벤트는 BE@RBRICK HEROES 회원가입을 완료한 회원에 한해 정상적으로 참여할 수 있습니다.</li>
                                <li>원활한 당첨자 확인 및 경품 안내를 위해, 회원가입 시 이메일 또는 휴대폰 번호 인증이 반드시 필요합니다.</li>
                                <li>회원가입 및 로그인 완료 후에도 이벤트 참여가 원활하지 않을 경우, 재로그인 후 다시 시도해주시기 바랍니다.</li>
                                <li>이벤트 경품은 이벤트 참여 시 로그인한 계정 정보 기준으로 지급됩니다.</li>
                                <li>경품 지급 및 당첨자 발표 시점에 계정 탈퇴 또는 회원 인증 정보 삭제로 인해 계정 정보 확인이 불가능한 경우, 당첨 및 경품 지급이 취소될 수 있습니다.</li>
                                <li>동일 명의의 복수 계정으로 여러 차례 참여한 경우, 동일 추첨 이벤트 내 중복 당첨은 불가하며, 당첨자 확인 과정에서 중복 당첨이 확인될 경우 당첨이 취소될 수 있습니다.</li>
                                <li>이벤트 참여 전, 로그인된 계정 정보를 반드시 확인해주시기 바랍니다.</li>
                                <li>비정상적이거나 부당한 방법으로 이벤트에 참여한 경우, 당첨 대상에서 제외되거나 당첨이 취소될 수 있습니다.</li>
                                <li>이벤트 상세 내용은 운영 상황에 따라 변경될 수 있으며, 변경 사항이 있을 경우 공식 홈페이지 이벤트 공지를 통해 안내드릴 예정입니다.</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
            <div className="underLineFx">
                <img className="underLine" src={process.env.PUBLIC_URL + "/images/Connect/MissionUnderLine.png"} alt="이벤트 구분선" />
                <img className="underLineMo" src={process.env.PUBLIC_URL + "/images/Connect/MissionUnderLineMo.png"} alt="이벤트 구분선" />
            </div>
            {resultModalOpen && (
                <div className="mdalCommWrap connectEventModalWrap useAlertMdal">
                    <div className="mdalBg" onClick={dismissResultModal}></div>
                    <div className="mdalCommContainer connectEventModalContainer">
                        <div className="mdalCommCloseBtn" onClick={dismissResultModal}>
                            <i className="close"></i>
                        </div>
                        <div className="mdalCtxtInfo connectEventModalMessage">
                            <h1 style={{display:"none"}}>{resultModalContent.h1}</h1>
                            <p
                                dangerouslySetInnerHTML={{
                                    __html: (resultModalContent.p || "").replace(/\n/g, "<br />"),
                                }}
                            ></p>
                        </div>
                        <button className="mdalCommAgreeBtn connectEventModalButton" onClick={closeResultModal}>
                            <p>{resultModalContent.btnLabel}</p>
                        </button>
                    </div>
                </div>
            )}

            {tcktHistModalOpen && (
                <div className="mdalCommWrap connectEventModalWrap">
                    <div className="mdalBg" onClick={closeTcktHistModal}></div>
                    <div className="mdalCommContainer connectEventModalContainer connectEventHistModalContainer">
                        <div className="mdalCommCloseBtn" onClick={closeTcktHistModal}>
                            <i className="close"></i>
                        </div>
                        <div className="mdalCtxtInfo connectEventModalMessage connectEventHistModalMessage">
                            <h1>응모 내역</h1>
                            {tcktSummaryState.tcktUseLst.length > 0 ? (
                                <ul className="connectEventHistList">
                                    {tcktSummaryState.tcktUseLst.map((hist, idx) => (
                                        <li key={`${hist.useDtime}-${hist.prdtNm}-${idx}`} className="connectEventHistItem">
                                            <p className="connectEventHistDate">{hist.useDtime}</p>
                                            <p className="connectEventHistName">{hist.prdtNm}</p>
                                            <p className="connectEventHistCount">-{hist.tcktCnt}개</p>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p>응모 내역이 없습니다.</p>
                            )}
                        </div>
                        <button className="mdalCommAgreeBtn connectEventModalButton" onClick={closeTcktHistModal}>
                            <p>닫기</p>
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}



export default CafeEvntUi;